// NodeNodelistSingleModal.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useBase } from 'context/BaseContext';

// Utilities
import {
  getNodelistNodeTitle,
  getNodelistPrimaryKey,
} from 'utilities/NodelistField';
import { getNodeSubtitle } from 'utilities/NodeSubtitle';
import { resolveNodeIcon } from 'utilities/resolveNodeIcon';

// Hooks
import useNodesByKeysQuery from 'hooks/useNodesByKeysQuery';
import useNodelistTargetOptions from 'node/form/fields/nodelist/useNodelistTargetOptions';
import useRelationQuickAdd from 'node/form/fields/nodelist/useRelationQuickAdd';

// Components
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';
import SelectInput from 'components/selectinput/SelectInput';
import RelationQuickAddModal from 'node/form/fields/nodelist/RelationQuickAddModal';

/**
 * NodeNodelistSingleModal
 *
 * Modal editor for single-select Relation fields.
 */
const NodeNodelistSingleModal = ({
  fieldLabel,
  isOpen,
  node,
  setOpen,
  showFilter = false,
  targetType,
  value,
  onChange,
}) => {
  const { getType } = useNodeTypes();
  const { activeBase } = useBase();

  const [draftValue, setDraftValue] = useState('');

  const baseKey = node?.baseKey || activeBase?.key || null;
  const selectedKey = getNodelistPrimaryKey(value);

  const {
    options: targetOptions,
    nodesByKey,
    resolveOptionValue,
  } = useNodelistTargetOptions({
    targetType,
    baseKey,
    enabled: Boolean(isOpen && targetType && baseKey),
  });

  const { nodes: selectedNodes } = useNodesByKeysQuery(selectedKey ? [selectedKey] : [], {
    enabled: Boolean(isOpen && selectedKey),
  });

  useEffect(() => {
    if (!isOpen) return;
    setDraftValue(selectedKey);
  }, [isOpen, selectedKey]);

  const selectedNode = useMemo(() => {
    if (!selectedKey) return null;
    if (nodesByKey.has(selectedKey)) return nodesByKey.get(selectedKey);
    return Array.isArray(selectedNodes) ? selectedNodes[0] : null;
  }, [nodesByKey, selectedKey, selectedNodes]);

  const baseSelectOptions = useMemo(() => {
    const emptyOption = { value: '', label: '', iconKey: '', note: '' };
    const byValue = new Map(targetOptions.map((option) => [option.value, option]));

    if (selectedKey && !byValue.has(selectedKey)) {
      const typeDef = targetType ? getType(targetType) : null;
      const { iconKey } = resolveNodeIcon({ node: selectedNode, typeDef });
      byValue.set(selectedKey, {
        value: selectedKey,
        label: selectedNode ? getNodelistNodeTitle(selectedNode) : 'Unavailable',
        iconKey: iconKey || '',
        note: selectedNode ? (getNodeSubtitle(selectedNode) || '') : 'No longer available',
      });
    }

    return [emptyOption, ...byValue.values()];
  }, [getType, selectedKey, selectedNode, targetOptions, targetType]);

  const handleSelect = useCallback((nextValue) => {
    setDraftValue(String(nextValue || ''));
  }, []);

  const {
    footerActionLabel,
    onFooterAction,
    selectOptions,
    quickAddOpen,
    setQuickAddOpen,
    quickAddInitialTitle,
    handleCreated,
  } = useRelationQuickAdd({
    readOnly: !isOpen,
    targetType,
    baseKey,
    options: baseSelectOptions,
    onSelect: handleSelect,
  });

  const handleSave = async () => {
    const raw = String(draftValue || '').trim();
    if (!raw) {
      onChange?.([]);
      setOpen?.(false);
      return;
    }
    try {
      const nextKey = await resolveOptionValue(raw);
      onChange?.(nextKey ? [nextKey] : []);
      setOpen?.(false);
    } catch (error) {
      console.error('[NodeNodelistSingleModal] catalog adopt failed:', error);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => setOpen?.(false)}
        title={fieldLabel || 'Related Item'}
        width={420}
      >
        <ModalForm>
          <SelectInput
            value={draftValue}
            options={selectOptions}
            placeholder="Select item..."
            emptyText="No items available"
            showFilter={showFilter === true}
            footerActionLabel={footerActionLabel}
            onFooterAction={onFooterAction}
            onValueChange={handleSelect}
            width="100%"
          />
        </ModalForm>

        <ModalButtons>
          <Button label="Save" onClick={handleSave} />
        </ModalButtons>
      </Modal>

      <RelationQuickAddModal
        isOpen={quickAddOpen}
        setOpen={setQuickAddOpen}
        typeKey={targetType}
        baseKey={baseKey}
        parentNode={node}
        initialTitle={quickAddInitialTitle}
        onCreated={handleCreated}
      />
    </>
  );
};

// Dev Tools
NodeNodelistSingleModal.displayName = 'NodeNodelistSingleModal';

export default NodeNodelistSingleModal;
